import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { FLEET_HOME } from "@/lib/locations";
import { PageShell, btnDanger, btnGhost, btnPrimary, cellCls, headCls, inputCls } from "@/components/TopBar";

const EMPTY = { plate_number: "", assigned_driver_id: "" };

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [v, d] = await Promise.all([api.get("/vehicles"), api.get("/drivers")]);
    setVehicles(v.data);
    setDrivers(d.data);
  }, []);

  useEffect(() => {
    load().catch(() => toast.error("Could not load vehicles"));
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.plate_number.trim()) return toast.error("Plate number is required");
    setBusy(true);
    try {
      const preset = FLEET_HOME;
      await api.post("/vehicles", {
        plate_number: form.plate_number.trim().toUpperCase(),
        assigned_driver_id: form.assigned_driver_id || null,
        current_location: { lat: preset.lat, lng: preset.lng },
        current_location_name: null,
      });
      toast.success("Vehicle added");
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id, body) => {
    try {
      await api.patch(`/vehicles/${id}`, body);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete ${v.plate_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/vehicles/${v.id}`);
      toast.success("Vehicle deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <PageShell
      testid="vehicles-page"
      subtitle="administration"
      title="TRUCK MANAGEMENT"
      actions={
        <button data-testid="new-vehicle-btn" onClick={() => setShowForm((s) => !s)} className={btnPrimary}>
          <Plus size={12} /> {showForm ? "CLOSE" : "ADD TRUCK"}
        </button>
      }
    >
      {showForm && (
        <form
          onSubmit={create}
          data-testid="vehicle-form"
          className="mb-8 grid gap-4 border border-white/15 bg-[#121212] p-5 sm:grid-cols-3"
        >
          <div>
            <label className="overline mb-2 block">vehicle number</label>
            <input
              data-testid="vehicle-plate-input"
              value={form.plate_number}
              onChange={(e) => setForm({ ...form, plate_number: e.target.value.toUpperCase() })}
              placeholder="NL01AB1234"
              className={inputCls}
            />
          </div>
          <div>
            <label className="overline mb-2 block">assign driver</label>
            <select
              data-testid="vehicle-driver-input"
              value={form.assigned_driver_id}
              onChange={(e) => setForm({ ...form, assigned_driver_id: e.target.value })}
              className={inputCls}
            >
              <option value="">- unassigned -</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button type="submit" disabled={busy} data-testid="submit-vehicle-btn" className={`${btnPrimary} w-full py-2.5`}>
              {busy ? "saving" : "save truck"}
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto border border-white/10">
        <table className="w-full min-w-[680px]" data-testid="vehicles-table">
          <thead className="border-b border-white/10 bg-[#121212]">
            <tr>
              <th className={headCls}>vehicle number</th>
              <th className={headCls}>driver</th>
              <th className={headCls}>status</th>
              <th className={headCls}>actions</th>
            </tr>
          </thead>
          <tbody>
            {vehicles.map((v) => (
              <tr key={v.id} data-testid={`vehicle-row-${v.plate_number}`} className="border-b border-white/5 hover:bg-[#141414]">
                <td className={`${cellCls} font-head font-bold`}>{v.plate_number}</td>
                <td className={cellCls}>
                  <select
                    data-testid={`vehicle-driver-select-${v.plate_number}`}
                    value={v.assigned_driver_id || ""}
                    onChange={(e) => patch(v.id, { assigned_driver_id: e.target.value || null })}
                    className={inputCls}
                  >
                    <option value="">- unassigned -</option>
                    {drivers.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className={cellCls}>
                  <select
                    data-testid={`vehicle-status-select-${v.plate_number}`}
                    value={v.status}
                    onChange={(e) => patch(v.id, { status: e.target.value })}
                    className={inputCls}
                  >
                    <option value="idle">idle</option>
                    <option value="on_trip">on_trip</option>
                    <option value="maintenance">maintenance</option>
                    <option value="inactive">inactive</option>
                  </select>
                </td>
                <td className={cellCls}>
                  <div className="flex gap-2">
                    <button
                      data-testid={`deactivate-vehicle-btn-${v.plate_number}`}
                      onClick={() => patch(v.id, { status: v.status === "inactive" ? "idle" : "inactive" })}
                      className={btnGhost}
                    >
                      {v.status === "inactive" ? "ACTIVATE" : "DEACTIVATE"}
                    </button>
                    <button
                      data-testid={`delete-vehicle-btn-${v.plate_number}`}
                      onClick={() => remove(v)}
                      className={btnDanger}
                      aria-label="Delete vehicle"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageShell>
  );
}
